import { useCallback, useEffect, useRef, useState } from "react";
import {
    PushNotifications,
    type PermissionStatus,
} from "@capacitor/push-notifications";
import type { PluginListenerHandle } from "@capacitor/core";
import { mobileDevices } from "../lib/api";
import { isNativeIOS } from "../lib/native-auth";

type PushState = PermissionStatus["receive"] | "registering" | "unavailable" | "error";

const INSTALLATION_KEY = "kardoe-os.native.installation-id";

function installationId(): string {
    const existing = localStorage.getItem(INSTALLATION_KEY);
    if (existing) return existing;
    const created = crypto.randomUUID();
    localStorage.setItem(INSTALLATION_KEY, created);
    return created;
}

function notificationRoute(data: Record<string, unknown> | undefined): string | null {
    const route = data?.route;
    if (typeof route !== "string" || !route.startsWith("/") || route.startsWith("//")) return null;
    return route.replace(/^\/mail\/inbox(?=\/|$)/, "/inbox");
}

export function useNativePush(options: {
    enabled: boolean;
    onOpenRoute: (route: string) => void;
    onRegistered?: () => void;
    onError?: (message: string) => void;
}) {
    const { enabled, onOpenRoute, onRegistered, onError } = options;
    const native = isNativeIOS();
    const [state, setState] = useState<PushState>(native ? "prompt" : "unavailable");
    const requestedRef = useRef(false);

    const reportError = useCallback((message: string) => {
        if (requestedRef.current) onError?.(message);
        else console.error("Native push registration failed", message);
        requestedRef.current = false;
    }, [onError]);

    useEffect(() => {
        if (!native || !enabled) return;
        const handles: PluginListenerHandle[] = [];
        let disposed = false;

        const listen = async () => {
            const registered = await Promise.all([
                PushNotifications.addListener("registration", token => {
                    setState("registering");
                    void mobileDevices.register({
                        installationId: installationId(),
                        platform: "ios",
                        pushToken: token.value,
                        environment: (import.meta.env.VITE_APNS_ENVIRONMENT as string | undefined) === "production"
                            ? "production"
                            : "development",
                    }).then(result => {
                        if (!result.ok) throw new Error(result.error ?? "Device registration failed.");
                        setState("granted");
                        if (requestedRef.current) onRegistered?.();
                        requestedRef.current = false;
                    }).catch(error => {
                        setState("error");
                        reportError(error instanceof Error ? error.message : String(error));
                    });
                }),
                PushNotifications.addListener("registrationError", error => {
                    setState("error");
                    reportError(error.error);
                }),
                PushNotifications.addListener("pushNotificationActionPerformed", action => {
                    const route = notificationRoute(action.notification.data);
                    if (route) onOpenRoute(route);
                }),
            ]);
            if (disposed) {
                for (const handle of registered) void handle.remove();
                return;
            }
            handles.push(...registered);

            const permission = await PushNotifications.checkPermissions();
            if (disposed) return;
            setState(permission.receive);
            // Re-register an already-authorized installation on each launch so
            // APNs token rotation is reflected server-side. Never prompt here.
            if (permission.receive === "granted") await PushNotifications.register();
        };

        void listen().catch(error => {
            setState("error");
            reportError(error instanceof Error ? error.message : String(error));
        });

        return () => {
            disposed = true;
            for (const handle of handles) void handle.remove();
        };
    }, [native, enabled, onOpenRoute, onRegistered, reportError]);

    const requestPermission = useCallback(async () => {
        if (!native) return;
        requestedRef.current = true;
        setState("registering");
        try {
            let permission = await PushNotifications.checkPermissions();
            if (permission.receive === "prompt") {
                permission = await PushNotifications.requestPermissions();
            }
            setState(permission.receive);
            if (permission.receive !== "granted") {
                reportError("Notifications are disabled. You can enable them in iOS Settings.");
                return;
            }
            await PushNotifications.register();
        } catch (error) {
            setState("error");
            reportError(error instanceof Error ? error.message : String(error));
        }
    }, [native, reportError]);

    return { isNative: native, state, requestPermission };
}
